import React, { useState } from 'react';
import { CreditCards } from 'src/components/credit-cards';
import { Currency } from 'src/components/currency';
import { InstallmentsCalculatorCard } from 'src/components/installments-calculator-card';
import { InstallmentsInput } from 'src/components/installments-input';
import { LearnMoreHowItWorks } from 'src/components/learn-more-how-it-works';
import { PayNextMonthTab } from 'src/components/pay-next-month-tab';
import { PaymentOptionsList } from 'src/components/payment-options-list';
import { PopupLearnMoreBackgroundHeader } from 'src/components/popup-learn-more-background-header';
import { TrustBanner } from 'src/components/trust-banner';
import { Button } from 'src/components/v2/button';
import { ACTIONS } from 'src/constants/common';
import { COUNTRY_BY_CURRENCY_MAP } from 'src/constants/country-by-currency-map';
import {
  INITIAL_PRICE,
  INSTALLMENT_PLANS,
  MIN_LTF_PRICE,
} from 'src/constants/learn-more';
import { useAppContext } from 'src/context/store/app-provider';
import { useLocale } from 'src/hooks/use-locale';
import { createNormalizeCheckingNumber } from 'src/utils/create-normalize-checking-number';
import {
  calculateFeesByPrice,
  calculatePriceWithFees,
} from 'src/utils/default-fees-calculator';
import { getFormattedAmount } from 'src/utils/get-formatted-amount';
import { getInstallmentPeriod } from 'src/utils/get-installment-period';
import { getLearnMoreBackgroundTitle } from 'src/utils/get-learn-more-background-title';
import { getPaymentText } from 'src/utils/get-payment-text';
import { getPriceSplitOf } from 'src/utils/get-price-split-of';
import { getSplitYourPurchaseText } from 'src/utils/get-split-purchase-text';

import {
  InstallmentItem,
  type InstallmentItemProps,
} from './components/installment-item';
import { InstallmentItemSkeleton } from './components/installment-item-skeleton';
import { SubventionFaqLink } from './components/subvention-faq-link';
import styles from './LongTermContent.css';
import {
  getFeesDescription,
  getIsLimitedRamadanOffer,
  getIsShowNoInterest,
  getSortedInstallmentsOffers,
} from './utils';

interface Props {
  onClose?: () => void;
  onContinue?: () => void;
}

type InstallmentOption = InstallmentItemProps & { key: React.Key };

export const LongTermContent = ({ onClose, onContinue }: Props) => {
  const {
    currency,
    offers: { installments: offersInstallments = [] },
    priceRaw,
    price,
    getExperiment,
    loading,
    dispatch,
  } = useAppContext();

  const t = useLocale();
  const country = COUNTRY_BY_CURRENCY_MAP[currency];
  const initialValue = (price || INITIAL_PRICE).toString();
  const [inputValue, setValue] = useState(initialValue);
  const isPlansSwitcherEnabled =
    getExperiment('CT-3300-plans-switcher') === 'test';
  const isPlanSwitcher =
    isPlansSwitcherEnabled && offersInstallments.length > 1;

  const checkPrice = createNormalizeCheckingNumber(
    'Price argument is not a number',
    0
  );
  const isRawPriceValid = !!checkPrice(priceRaw);
  const initialPreparedPrice = priceRaw && isRawPriceValid ? priceRaw : price;
  const merchantHasPreConfiguredPlans = offersInstallments.length !== 0;
  const isCalculationEnabled = !isRawPriceValid;

  const longestInstallmentPlan = offersInstallments.reduce(
    (maxInstallmentPeriodPlan, installment) => {
      return installment.paymentsCount > maxInstallmentPeriodPlan.paymentsCount
        ? installment
        : maxInstallmentPeriodPlan;
    },
    offersInstallments[0]
  );

  const hasConfiguredPlans = offersInstallments.length !== 0;
  const hasMurabaha = Boolean(
    offersInstallments.some((item) => item.serviceFeePolicy === 'annuity') ||
      (offersInstallments.length === 0 && country === 'KSA')
  );

  const getInstallmentOptions = (): InstallmentOption[] => {
    const value = isCalculationEnabled
      ? Number(inputValue)
      : Number(initialPreparedPrice);
    const list = isCalculationEnabled;

    if (merchantHasPreConfiguredPlans) {
      const sortedInstallmentsOffers =
        getSortedInstallmentsOffers(offersInstallments);

      return sortedInstallmentsOffers.map<InstallmentOption>((item, idx) => {
        const {
          amount,
          paymentsCount,
          period,
          totalFees,
          serviceFeePolicy,
          isLimitedTimeOffer,
          subventionCampaignId,
        } = item;

        const paymentsInfo = t(
          'paid_plans.payment_per_period',
          {
            price: getFormattedAmount(amount, currency),
            period: getInstallmentPeriod(t, period),
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
        const feesAmount = totalFees
          ? getFormattedAmount(totalFees, currency)
          : null;

        const subtitle = getFeesDescription({
          feesAmount,
          totalFees,
          serviceFeePolicy,
          currency,
          period,
          fullAmount: value,
          paymentsCount,
          t,
        });

        const isShowNoInterest = getIsShowNoInterest({
          currency,
          serviceFeePercent: item.service_fee_percentage,
          serviceFeePolicy,
        });

        const isLimitedRamadanOfferOptions = getIsLimitedRamadanOffer({
          currency,
          paymentsCount,
          serviceFeePercent: item.service_fee_percentage,
          serviceFeePolicy,
        });

        const additionalContent = subventionCampaignId ? (
          <SubventionFaqLink offer={item} />
        ) : null;

        return {
          key: idx,
          title: getPaymentText(paymentsCount, t),
          subtitle,
          paymentsInfo,
          isGreenSubtitle: country !== 'KSA' && !feesAmount,
          isLimitedTimeOffer:
            isLimitedTimeOffer ||
            !!subventionCampaignId ||
            isLimitedRamadanOfferOptions,
          additionalContent,
          list,
          noInterest: isShowNoInterest ? t('no_interest') : null,
        };
      });
    }

    const defaultOptionsMap = {
      UAE: [
        {
          key: INSTALLMENT_PLANS.four,
          title: getPaymentText(INSTALLMENT_PLANS.four, t),
          subtitle:
            country === 'KSA' && !hasConfiguredPlans
              ? t('no_interest_processing_fee_may_apply')
              : t('paid_plans.no_interest_no_fees'),
          paymentsInfo: t(
            'paid_plans.payment_per_period',
            {
              price: getPriceSplitOf(value, currency, INSTALLMENT_PLANS.four),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          isGreenSubtitle: country !== 'KSA',
          list,
        },
        {
          key: INSTALLMENT_PLANS.six,
          title: getPaymentText(INSTALLMENT_PLANS.six, t),
          subtitle: t(
            'paid_plans.service_fee_in_total',
            {
              serviceFee: calculateFeesByPrice(
                value,
                INSTALLMENT_PLANS.six,
                currency
              ),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          paymentsInfo: t(
            'paid_plans.payment_per_period',
            {
              price: getPriceSplitOf(
                calculatePriceWithFees({
                  price: value,
                  installmentCount: INSTALLMENT_PLANS.six,
                }),
                currency,
                INSTALLMENT_PLANS.six
              ),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          isGreenSubtitle: false,
          list,
        },
        {
          key: INSTALLMENT_PLANS.eight,
          title: getPaymentText(INSTALLMENT_PLANS.eight, t),
          subtitle: t(
            'paid_plans.service_fee_in_total',
            {
              serviceFee: calculateFeesByPrice(
                value,
                INSTALLMENT_PLANS.eight,
                currency
              ),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          paymentsInfo: t(
            'paid_plans.payment_per_period',
            {
              price: getPriceSplitOf(
                calculatePriceWithFees({
                  price: value,
                  installmentCount: INSTALLMENT_PLANS.eight,
                }),
                currency,
                INSTALLMENT_PLANS.eight
              ),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          isGreenSubtitle: false,
          list,
        },
        {
          key: INSTALLMENT_PLANS.twelve,
          title: getPaymentText(INSTALLMENT_PLANS.twelve, t),
          subtitle: t(
            'paid_plans.service_fee_in_total',
            {
              serviceFee: calculateFeesByPrice(
                value,
                INSTALLMENT_PLANS.twelve,
                currency
              ),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          paymentsInfo: t(
            'paid_plans.payment_per_period',
            {
              price: getPriceSplitOf(
                calculatePriceWithFees({
                  price: value,
                  installmentCount: INSTALLMENT_PLANS.twelve,
                }),
                currency,
                INSTALLMENT_PLANS.twelve
              ),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          isGreenSubtitle: false,
          list,
        },
      ],
      KSA: [
        {
          key: INSTALLMENT_PLANS.three,
          title: getPaymentText(INSTALLMENT_PLANS.three, t),
          subtitle: t('paid_plans.no_interest_no_fees'),
          paymentsInfo: t(
            'paid_plans.payment_per_period',
            {
              price: getPriceSplitOf(value, currency, INSTALLMENT_PLANS.three),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          list,
        },
        {
          key: INSTALLMENT_PLANS.four,
          title: getPaymentText(INSTALLMENT_PLANS.four, t),
          subtitle: t('no_interest_processing_fee_may_apply'),
          paymentsInfo: t(
            'paid_plans.from_payment_per_period',
            {
              price: getPriceSplitOf(value, currency, INSTALLMENT_PLANS.four),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          list,
        },
        {
          key: INSTALLMENT_PLANS.six,
          title: getPaymentText(INSTALLMENT_PLANS.six, t),
          subtitle: t(
            'monthly_profit_may_apply',
            {
              minPrice: getFormattedAmount(MIN_LTF_PRICE, currency),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          paymentsInfo: t(
            'paid_plans.from_payment_per_period',
            {
              price: getPriceSplitOf(value, currency, INSTALLMENT_PLANS.six),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          list,
        },
        {
          key: INSTALLMENT_PLANS.eight,
          title: getPaymentText(INSTALLMENT_PLANS.eight, t),
          subtitle: t(
            'monthly_profit_may_apply',
            {
              minPrice: getFormattedAmount(MIN_LTF_PRICE, currency),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          paymentsInfo: t(
            'paid_plans.from_payment_per_period',
            {
              price: getPriceSplitOf(value, currency, INSTALLMENT_PLANS.eight),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          list,
        },
        {
          key: INSTALLMENT_PLANS.twelve,
          title: getPaymentText(INSTALLMENT_PLANS.twelve, t),
          subtitle: t(
            'monthly_profit_may_apply',
            {
              minPrice: getFormattedAmount(MIN_LTF_PRICE, currency),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          paymentsInfo: t(
            'paid_plans.from_payment_per_period',
            {
              price: getPriceSplitOf(value, currency, INSTALLMENT_PLANS.twelve),
              period: t('period'),
            },
            {
              currency: () => <Currency currency={currency} />,
            }
          ),
          list,
        },
      ],
    };

    return defaultOptionsMap[country] ?? defaultOptionsMap.UAE;
  };

  const onChange = (val: string) => {
    setValue(val);

    if (merchantHasPreConfiguredPlans) {
      dispatch({
        type: ACTIONS.UPDATE_STORE,
        payload: {
          loading: true,
          price: Number(val),
          skipRawPriceUpdate: true,
        },
      });
    }
  };

  const installmentOptions = loading ? [] : getInstallmentOptions();
  const paymentOptions = loading ? (
    <div className={styles.LongTermContent__Loading}>
      {Array.from({ length: 4 }).map((_, idx) => (
        <InstallmentItemSkeleton key={idx} />
      ))}
    </div>
  ) : (
    <PaymentOptionsList list={isCalculationEnabled}>
      {installmentOptions.map(({ key, ...option }) => (
        <InstallmentItem key={key} {...option} />
      ))}
    </PaymentOptionsList>
  );

  return (
    <div className={styles.LongTermContent}>
      <PopupLearnMoreBackgroundHeader
        onClose={onClose}
        title={getLearnMoreBackgroundTitle(currency, t)}
        subtitle={getSplitYourPurchaseText(
          longestInstallmentPlan?.paymentsCount ?? INSTALLMENT_PLANS.twelve,
          hasMurabaha,
          t,
          currency
        )}
      />

      {isCalculationEnabled ? (
        <InstallmentsCalculatorCard
          input={
            <InstallmentsInput
              id="long-term-input"
              value={inputValue}
              onChange={onChange}
            />
          }
        >
          {paymentOptions}
        </InstallmentsCalculatorCard>
      ) : (
        paymentOptions
      )}

      <PayNextMonthTab />

      <LearnMoreHowItWorks />

      {!isPlanSwitcher && (
        <>
          {country === 'KSA' ? (
            <TrustBanner
              title={t('shariah_compliant_payments')}
              description={t(
                'our_payment_options_meet_islamic_finance_standards'
              )}
              icon="shariat-modern"
              iconClassName={styles.LongTermContent__BannerIcon}
            />
          ) : (
            <TrustBanner
              title={t('tabby_trusted.trusted_by_milions')}
              description={t('over_20_million_shoppers_pay_with_tabby')}
              icon="people"
            />
          )}

          <TrustBanner
            title={t('tabby_care.show_safely')}
            description={t('tabby_care.buyer_protection_included')}
            icon="tabby-care"
          />
        </>
      )}

      {!!onContinue && (
        <Button
          onClick={onContinue}
          data-test="continue-shopping"
          className={styles.LongTermContent__Button}
        >
          {t('tabbySplit.continue_shopping')}
        </Button>
      )}

      <CreditCards country={country} fullWidth />
    </div>
  );
};
